import { useDebounceFn } from 'ahooks';
import { autorun } from 'mobx';
import { inject, observer } from 'mobx-react';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { I18n } from '@byted-cg/linkchat-plugin';

import { AppContextType, CombineMessageType } from '@byted-cg/linkchat-plugin';

import ErrorBoundary from '../components/ErrorBoundary';
import { Icon } from '../components/Icon';
import ScrollView from '../components/ScrollView';
import classes from '../constants/classes';
import { prefixCls } from '../utils/style';
import LazyComponent from './LazyComponent';
import MessageContainer from './MessageContainer';
import { IAvatarsConfig } from './type';

enum MessagePosition {
  Left = 'left',
  Center = 'center',
  Right = 'right',
  Fixed = 'fixed',
  Auto = 'auto',
  ToMessageList = 'to-messagelist',
  SingleToMessageListCard = 'single-to-messagelist',
}
const AutoCardList = [
  MessagePosition.Auto,
  MessagePosition.Fixed,
  MessagePosition.ToMessageList,
  MessagePosition.SingleToMessageListCard,
];
interface IMessageWrapperRef {
  scrollToBottom: (smooth?: boolean) => void;
}
export type IMessageWrapper = {
  appContext?: AppContextType;
  config?: IAvatarsConfig;
};
const initTimer = performance.now();
const MessageWrapper = React.forwardRef<IMessageWrapperRef, IMessageWrapper>(
  ({ appContext, config }, ref) => {
    const [list, setList] = useState<CombineMessageType[]>([]);
    const [isEnd, setIsEnd] = useState(false);
    autorun(() => setList(appContext?.messages || []), {
      delay: 60,
      name: 'messages',
      requiresObservable: true,
    });

    const scrollRef = useRef<any>(null);
    const [isBottom, setIsBottom] = useState(true);
    const thisDomRef = useRef<HTMLDivElement>(null);
    const destroyKeyRef = useRef<Record<string, boolean>>({});

    const setIsNew = useCallback(
      (key?: string) => (value?: boolean) => {
        if (key) {
          destroyKeyRef.current = {
            ...destroyKeyRef.current,
            [key]: value || false,
          };
        }
      },
      [],
    );

    const getIsNew = useCallback(
      (message: CombineMessageType) => () => {
        if (
          message._id &&
          typeof destroyKeyRef.current[message._id] === 'boolean'
        ) {
          return destroyKeyRef.current[message._id];
        }
        return message.isNew;
      },
      [],
    );

    // Render Fixed Messages
    const renderFixMessage = useCallback(
      (message: CombineMessageType) => {
        if (!message?.component || !message.position) return null;
        switch (message.position) {
          case MessagePosition.SingleToMessageListCard:
            return getSingleComponent(message);
          case MessagePosition.Auto:
            return (
              <LazyComponent
                fallback={false}
                key={message._id}
                data-kid={message._id}
                data-sid={message.indexInConversation?.toString()}
                setIsNew={setIsNew(message._id)}
                getIsNew={getIsNew(message)}
                config={message.component}
                message={message}
              />
            );
          case MessagePosition.ToMessageList:
            return (
              thisDomRef.current &&
              createPortal(
                <LazyComponent
                  fallback={false}
                  key={message._id}
                  data-kid={message._id}
                  data-sid={message.indexInConversation?.toString()}
                  config={message.component}
                  message={message}
                  setIsNew={setIsNew(message._id)}
                  getIsNew={getIsNew(message)}
                />,
                thisDomRef.current,
              )
            );
          default:
            return createPortal(
              <LazyComponent
                fallback={false}
                key={message._id}
                data-kid={message._id}
                data-sid={message.indexInConversation?.toString()}
                config={message.component}
                message={message}
                setIsNew={setIsNew(message._id)}
                getIsNew={getIsNew(message)}
              />,
              appContext?.mountId || document.body,
            );
        }
      },
      [appContext?.$refs],
    );

    // Scroll to the bottom after the first render
    const scrollToBottomAfterFirstRender = useRef<VoidFunction | null>(() => {
      // Wait for rendering to finish before scrolling to the bottom
      setTimeout(() => {
        scrollToBottom(false);
        onScrollToBottom();
      }, 0);
      scrollToBottomAfterFirstRender.current = null;
    });

    const { run: onScroll } = useDebounceFn(
      () => {
        if (scrollRef.current?.checkScrolledToBottom) {
          setIsBottom(scrollRef.current.checkScrolledToBottom(58));
        }
      },
      {
        wait: 300,
        // leading: true
      },
    );

    const backBottom = useCallback(() => {
      scrollToBottom()?.then?.(() => setIsBottom(true));
    }, []);

    const onScrollStart = () => {
      if (!scrollToBottomAfterFirstRender.current) {
        appContext?.$refs.$usePanel?.current?.toggle(false);
      }
    };

    // Scroll to the bottom
    const scrollToBottom = (smooth = true) => {
      return scrollRef.current?.scrollToBottom(smooth);
    };

    const onScrollToBottom = () => {
      if (list.length) {
        appContext?.channel?.read();
      }
    };

    const onScrollToTop = async (cb: any) => {
      try {
        if (appContext?.channel?.pullMessages) {
          const { hasMore, messages } = await appContext.channel?.pullMessages(
            {},
          );
          setIsEnd(!hasMore);
          if (!hasMore) {
            cb();
          }
          if (
            messages &&
            messages.length &&
            messages.every(
              (v) =>
                v.content && v.content.indexOf('fixed-message-list-card') > -1,
            )
          ) {
            onScrollToTop(cb);
          }
        }
      } catch (error) {
        /** 兼容异常逻辑 */
        setIsEnd(false);
        cb();
      }
    };

    useImperativeHandle(ref, () => {
      return {
        scrollToBottom,
      };
    });

    const observeRef = useRef<IntersectionObserver[]>();
    const observeTimer = useRef<number>(0);
    const getSingleComponent = useCallback((message: CombineMessageType) => {
      const list = appContext?.messages || [];
      const filterList = list.filter(
        (item) => item.component?.code === message.component?.code,
      );
      if (
        filterList.length &&
        filterList[filterList.length - 1] &&
        filterList[filterList.length - 1]._id === message._id &&
        thisDomRef.current
      ) {
        return createPortal(
          <LazyComponent
            fallback={false}
            key={message._id}
            data-kid={message._id}
            data-sid={message.indexInConversation?.toString()}
            config={message.component as IComponent}
            message={message}
            setIsNew={setIsNew(message._id)}
            getIsNew={getIsNew(message)}
          />,
          thisDomRef.current,
        );
      }
      return null;
    }, []);

    useEffect(() => {
      if (list?.length && scrollToBottomAfterFirstRender.current) {
        /** 首屏消息列表完全渲染时间间隔 */
        appContext?.utils?._monitor?.emitDelay(
          'messageListRender',
          Number((performance.now() - initTimer).toFixed(2)),
          {
            msglength: list.length,
            firstId: list[0]?.serverId,
          },
        );
        scrollToBottomAfterFirstRender.current();
      }
      observeTimer.current && window.clearTimeout(observeTimer.current);
      observeTimer.current = window.setTimeout(() => {
        observeRef.current?.forEach((item) => item?.disconnect());
        observeRef.current = [];
        const messageNodeList = document.querySelectorAll('div[data-sid]');
        const lastNodeList = Array.prototype.slice.call(messageNodeList, -3);
        if (lastNodeList && lastNodeList.length) {
          lastNodeList.forEach((node) => {
            let observeNode = new IntersectionObserver(
              (entries) => {
                if (entries && entries.length) {
                  const intersectionRatio = entries[0].intersectionRatio;
                  if (intersectionRatio > 0.3 && intersectionRatio <= 1) {
                    appContext?.channel?.read &&
                      appContext.channel?.read(node.dataset['sid']);
                    observeNode && observeNode.disconnect();
                  }
                }
              },
              {
                threshold: 0.3,
              },
            );
            observeNode.observe(node);
            observeRef.current?.push(observeNode);
          });
        }
      }, 300);
      return () => {
        observeRef.current?.forEach((item) => item?.disconnect());
        observeRef.current = [];
      };
    }, [list.length]);
    return (
      <div
        className={prefixCls(
          {
            [`${classes.prefix}MessageWrapper`]: true,
          },
          appContext?.query?.isPc,
        )}
        ref={thisDomRef}
      >
        <ScrollView
          ref={scrollRef}
          enableOnScrollToTop={true && !isEnd}
          onScrollToTop={onScrollToTop}
          onPullToRefresh={onScrollToTop}
          enableOnScrollToBottom={true}
          enableOnPullToRefresh={true}
          ScrollToBottomLoading={() => null}
          onScrollToBottom={onScrollToBottom}
          onScroll={onScroll}
          onScrollStart={onScrollStart}
        >
          {list &&
            list.map((message: CombineMessageType) => {
              if (message.hide) {
                return null;
              }
              return message.position &&
                AutoCardList.indexOf(message.position as MessagePosition) >
                  -1 ? (
                renderFixMessage(message)
              ) : (
                <ErrorBoundary key={message._id || message.clientId}>
                  <MessageContainer
                    avatarsConfig={appContext?.renderConfig?.avatars}
                    key={message._id || message.clientId}
                    message={message}
                    appContext={appContext}
                  />
                </ErrorBoundary>
              );
            })}
        </ScrollView>
        {!isBottom ? (
          !!appContext?.dialog?.unreadCount ? (
            <span
              onClick={backBottom}
              className="scrollToBottom scrollToBottom__newMsg"
            >
              <Icon className="scrollToBottomIcon" type="DoubleDown" />
              {I18n.t('linkchat_new_message', {}, '新消息')}
            </span>
          ) : (
            <span onClick={backBottom} className="scrollToBottom">
              <Icon className="scrollToBottomIcon" type="DoubleDown" />
              {I18n.t('linkchat_go_back', {}, '回到最后')}
            </span>
          )
        ) : null}
      </div>
    );
  },
);
export default inject<{
  appContext: AppContextType;
}>(({ appContext }) => ({
  appContext,
}))(observer(MessageWrapper));
